import PropTypes from 'prop-types';
import React from 'react';
import { observer } from 'mobx-react';
import { getIn, useFormikContext } from 'formik';
import { makeStyles } from '@material-ui/core/styles';

import FormList from 'core/components/form-list';
import { useDisabledState } from 'common/disabled-context';
import {
  getLocationOptions,
  getEquipmentOptions,
  getRiskOptions,
  getSppOptions,
  getRpsOptions,
  getFarmPolicyMask,
  getAddInterestedPartyLabel
} from 'common/helpers/farm-helpers';
import { Locations } from './locations';
import { PolicyWideEndorsements } from './policy-wide-endorsements';
import { LiabilityCoverages } from './liability-coverages';
import { getMortgageStructureOptions } from 'common/helpers/farm-mortgage-helpers';
import { AddFarmInterestedPartyForm, FarmInterestedParty } from './additional-party';
import { AddMortgage, MortgageItem } from '../mortgage-details';

const useStyles = makeStyles((theme) => ({
  innerContainer: {
    padding: theme.spacing(8),
    backgroundColor: theme.colors.card_background_primary_editable,
    borderRadius: theme.sizes.roundCorners
  }
}));

function FarmOffer({ section = 'all' }) {
  const { values } = useFormikContext();
  const { disabled } = useDisabledState();
  const classes = useStyles();

  const locationOptions = getLocationOptions(values.farmLocations);
  const equipmentOptions = getEquipmentOptions(values.farmLocations);

  // Resolve the policy type and state for mask lookups the same way the liability sub-sections do.
  // The product lives on `policyType` in the policy form but on `selectedOption` on offer surfaces,
  // and the offer record carries no top-level `state` — it lives on the corrected address / first
  // farm location. Reading the bare `values.policyType`/`values.state` here meant the mask never
  // resolved on the offer route (it fell through to null / the full-Farm fallback), so Home (Z) and
  // Rental (Y) showed the generic "Additional Interests/Insureds" title and leaked GL-4002 into the
  // Additional Insured/Interest dropdown. (#32165; mirrors liability-coverages.jsx)
  const farmPolicyType = values.policyType || values.selectedOption;
  const state = getIn(values, 'correctedAddress.state') || getIn(values, 'farmLocations.0.address.state');

  const riskOptions = getRiskOptions(values.farmLocations, farmPolicyType, state);
  const sppOptions = getSppOptions(values.farmLocations);
  const rpsOptions = getRpsOptions(values.farmLocations);

  // "Applies to structure" dropdown options for mortgages — only mortgageable structures (owned
  // dwellings FO-1/2/3 and FO-6 barns/outbuildings), reusing the risk-option labels. (#32526)
  const structureOptions = getMortgageStructureOptions(values.farmLocations, riskOptions);

  const includeFarmLiability = values.includeFarmLiability;
  const liabilityType = values.farmLiabilityCoverage?.type;

  // Title for the interested-party list comes from the policy-type mask so Forreston Rental (Y)
  // and Home (Z) show "Additional Insured/Interest". (#31367)
  const farmPolicyMask = getFarmPolicyMask(farmPolicyType, state);
  const interestedPartyTitle = farmPolicyMask?.interestedPartyLabel || 'Additional Interests/Insureds';
  // "Farm" is dropped from the add-party button on Home (Z) / Rental (Y); kept for full Farm. (#32045)
  const addInterestedPartyLabel = getAddInterestedPartyLabel(farmPolicyType);
  // Relationships the mask removes from the dropdown for the current liability type
  // (e.g. GL-4002 on rentals, #32043).
  const hiddenRelationships = farmPolicyMask?.hiddenInterestedPartyRelationships?.[liabilityType] || [];
  // FO-41 add-on toggle is removed entirely on Rental (#32359).
  const hideFO41 = Boolean(farmPolicyMask?.hideFO41);
  // Loss Payee "Associated equipment" field is dropped on Home/Rental (#32521/#32525); full Farm
  // keeps it (#32520).
  const hideLossPayeeEquipment = Boolean(farmPolicyMask?.hideLossPayeeEquipment);

  return (
    <>
      <Locations />
      {section !== 'details' && (
        <>
          <LiabilityCoverages />
          <PolicyWideEndorsements />
        </>
      )}
      {section !== 'coverage' && (
        <>
          <FormList
            key={`parties-${disabled}`}
            id="farmInterestedParties"
            item={FarmInterestedParty}
            addForm={AddFarmInterestedPartyForm}
            addButtonLabel={addInterestedPartyLabel}
            title={interestedPartyTitle}
            singular="Interest/Insured"
            plural="Interests/Insureds"
            disabled={disabled}
            hiddenRelationships={hiddenRelationships}
            hideFO41={hideFO41}
            hideLossPayeeEquipment={hideLossPayeeEquipment}
            locationOptions={locationOptions}
            equipmentOptions={equipmentOptions}
            riskOptions={riskOptions}
            sppOptions={sppOptions}
            rpsOptions={rpsOptions}
            includeFarmLiability={includeFarmLiability}
            liabilityType={liabilityType}
            existingParties={values.farmInterestedParties || []}
          />
          <FormList
            classes={classes}
            id="farm.mortgageDetails"
            item={MortgageItem}
            addForm={AddMortgage}
            title="Mortgage lender information"
            singular="Mortgage"
            plural="Mortgages"
            disabled={disabled}
            structureOptions={structureOptions}
          />
        </>
      )}
    </>
  );
}
FarmOffer.propTypes = { section: PropTypes.oneOf(['all', 'details', 'coverage']) };
export default observer(FarmOffer);
